import maybeUpdate11AgentData from '../../ecdn11/maybeUpdate11AgentData';
import { Playback } from '../constants';
import InterceptorName from '../../interceptor/InterceptorName';
import isValidGuid from '../../util/isValidGuid';
import checkForInterceptor from '../../util/checkForInterceptor';
import logger from '../../log';
import { updateInitialReportFromRecord } from './report/updateInitialReport';
import { use10Reporting } from './formatReportRequest';
import { ReporterReadyInterface } from './interfaces';
const log = logger.getLogger(__filename);

type RSU = Record<string, unknown>;

export const checkForMissingFields = (
  errorList: string[],
  toCheck: string[],
  item: unknown,
  name: string
): void => {
  const errors: string[] = [];
  toCheck.forEach(key => {
    if (!(item as RSU)[key]) {
      errors.push(key);
    }
  });
  if (errors.length > 0) {
    errorList.push(`${name} missing fields: ${errors.join(', ')}`);
  }
};

export const checkGuid = (
  errors: string[],
  name: string,
  value?: string
): void => {
  if (!isValidGuid(value)) {
    const v = typeof value === 'undefined' ? `${value}` : `'${value}'`;
    errors.push(`${name}: ${v}`);
  }
};

export const checkGeneral = (
  errors: string[],
  reporter: ReporterReadyInterface,
  checkConfigAndLocalityData: boolean
): void => {
  const { general, localityData } = reporter.initialReport;
  // artGuid, title, and o365 also required under certain conditions but not
  // checked here.
  const toCheck = [
    'config',
    'playType',
    'playGuid',
    'playback',
    'browserUserGuid',
    'integration'
  ];
  if (checkConfigAndLocalityData) {
    toCheck.push('config');
  }
  checkForMissingFields(errors, toCheck, general, 'general');
  if (checkConfigAndLocalityData && typeof localityData === 'undefined') {
    errors.push('missing localityData');
  }
};

export const checkWebrtcState = (
  hasWebRTC: boolean,
  errors: string[],
  reporter: ReporterReadyInterface
): void => {
  const webrtc = reporter.initialReport.ecdn11?.webrtc;
  if (!hasWebRTC) {
    return;
  }
  if (!webrtc) {
    errors.push('required webrtc object missing');
    return;
  }
  checkGuid(errors, 'webrtc.activeClusterId', webrtc.activeClusterId);
  const toCheck = ['v', 'agentId', 'nodeId'];
  checkForMissingFields(errors, toCheck, webrtc, 'webrtc');
};

export const checkLocalAgentState = (
  hasLocalAgent: boolean,
  errorList: string[],
  reporter: ReporterReadyInterface
): void => {
  if (!hasLocalAgent) {
    return;
  }
  const errors: string[] = [];
  const { initialReport } = reporter;
  const agent = initialReport?.ecdn11?.agent;
  if (!agent) {
    errors.push('No agent object available');
  } else {
    const toCheck = ['v', 'agentId', 'nodeId', 'machineName'];
    checkForMissingFields(errors, toCheck, agent, 'agent');
    checkGuid(errors, 'webrtc.activeClusterId', agent.activeClusterId);
  }
  if (errors.length > 0) {
    log.info('checking for missing 11 agent data');
    maybeUpdate11AgentData(reporter.localState, reporter.pending);
    errorList.push(...errors);
  }
};

export const updateReport = (reporter: ReporterReadyInterface): void => {
  const { initialReport } = reporter;
  // Get any fields from the pending report
  updateInitialReportFromRecord(initialReport, reporter.pending);
  if (reporter.state.activeClusterId) {
    const webrtc = initialReport?.ecdn11?.webrtc;
    if (webrtc) {
      webrtc.activeClusterId = reporter.state.activeClusterId;
    }
  }
};

const hasRequiredStateForInitialReport = (
  reporter: ReporterReadyInterface
): boolean => {
  const { localState: state } = reporter;
  const { playType, activeMode } = state.play;
  if (!playType || !activeMode) {
    return false;
  }
  updateReport(reporter);

  const hasWebRTC = checkForInterceptor(InterceptorName.WEBRTC_AGENT, state);
  const hasLocalAgent = checkForInterceptor(InterceptorName.LOCAL_AGENT, state);
  const checkConfigAndLocalityData = hasWebRTC || hasLocalAgent;
  const errors: string[] = [];
  checkGeneral(errors, reporter, checkConfigAndLocalityData);
  checkWebrtcState(hasWebRTC, errors, reporter);
  checkLocalAgentState(hasLocalAgent, errors, reporter);

  const { playback } = reporter.initialReport.general;
  if (errors.length > 0) {
    log.debug('Missing required state to start reporter', errors.join('; '));
    return false;
  }
  if (playback === Playback.ECDN10 || playback === Playback.CDN) {
    return use10Reporting() ? true : !!reporter.initialReport.general.config;
  }
  return true;
};

export default hasRequiredStateForInitialReport;
